/**
 * FloatingDialer — global mini-softphone overlay (Yeastar Linkus-style).
 *
 * Lives in the Layout footer, anchored to the bottom-right of the viewport.
 * Consumes the shared SoftphoneContext so:
 *   - Click-to-call from /contacts (in this tab) routes directly here, no popup.
 *   - Incoming SIP INVITEs surface as a ring-banner regardless of route.
 *   - Calls survive page navigation (audio sink lives in the provider).
 *
 * Collapsed state: 56px status pill (color reflects phase) — click to expand.
 * Expanded state: 320×480px floating panel with status, remote-id, dest input,
 *   compact dialpad (3 columns × 4 rows), and call/answer/hangup button.
 *
 * The full /softphone page remains the "control center" view with extra
 * controls (mute / hold / DTMF buttons).
 */
import React, { useState, useEffect } from 'react';
import {
    Phone, PhoneSlash, PhoneIncoming, X, Backspace,
    CaretRight, ArrowsOut, SpinnerGap, CheckCircle, WarningCircle, UserCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useSoftphone } from '@/context/SoftphoneContext';

const PHASE_COLORS = {
    idle: { c: '#64748b', label: 'OFFLINE' },
    connecting: { c: '#f59e0b', label: 'PŘIPOJUJI' },
    registered: { c: '#22c55e', label: 'ONLINE' },
    failed: { c: '#ef4444', label: 'CHYBA' },
    ringing: { c: '#f59e0b', label: 'VYZVÁNÍ' },
    in_call: { c: '#22c55e', label: 'HOVOR' },
    ended: { c: '#64748b', label: 'KONEC' },
};

const DIAL_ROWS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#'],
];

const fmt = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const PHASE_ICON = (phase) => {
    if (phase === 'connecting') return <SpinnerGap size={14} className="animate-spin" />;
    if (phase === 'failed') return <WarningCircle size={14} weight="fill" />;
    if (phase === 'registered' || phase === 'in_call') return <CheckCircle size={14} weight="fill" />;
    if (phase === 'ringing') return <PhoneIncoming size={14} weight="fill" className="animate-pulse" />;
    return <UserCircle size={14} />;
};

const FloatingDialer = () => {
    const navigate = useNavigate();
    const {
        creds, credsError, phase, dest, setDest, duration, remote, hasIncoming,
        register, placeCall, hangup, acceptCall, rejectCall, sendDtmf,
    } = useSoftphone();
    const [open, setOpen] = useState(false);

    // Auto-open the panel whenever there's any softphone activity:
    // - incoming call (ringing)
    // - active call (in_call)
    // - outbound dial in progress (connecting → registered if we just clicked a contact)
    // - any populated dest input (user started typing or click-to-call queued it)
    useEffect(() => {
        if (
            phase === 'ringing' || phase === 'in_call' || phase === 'connecting'
            || (phase === 'registered' && dest.trim())
        ) {
            setOpen(true);
        }
    }, [phase, dest]);

    // Hide the entire widget for users with no extension assigned.
    if (credsError && !creds) return null;

    const color = PHASE_COLORS[phase] || PHASE_COLORS.idle;
    const inCall = phase === 'in_call' || phase === 'ringing';

    // ---- Collapsed pill (just a status chip) -----------------------------
    if (!open) {
        return (
            <button
                data-testid="floating-dialer-pill"
                onClick={() => setOpen(true)}
                aria-label="Otevřít mini-softphone"
                className="fixed z-40 flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] shadow-2xl"
                style={{
                    bottom: 16,
                    right: 16,
                    borderRadius: 999,
                    background: `linear-gradient(135deg, ${color.c}30, rgba(0,0,0,0.85))`,
                    border: `1px solid ${color.c}`,
                    color: color.c,
                    boxShadow: `0 0 18px ${color.c}55`,
                    backdropFilter: 'blur(8px)',
                    transition: 'transform 120ms',
                }}
            >
                {PHASE_ICON(phase)}
                <span>{color.label}</span>
                {creds?.extension && (
                    <span className="font-mono opacity-80">· {creds.extension}</span>
                )}
                {phase === 'in_call' && (
                    <span className="font-mono opacity-80">· {fmt(duration)}</span>
                )}
            </button>
        );
    }

    // ---- Expanded mini-phone panel --------------------------------------
    return (
        <div
            data-testid="floating-dialer-panel"
            className="fixed z-40 flex flex-col shadow-2xl"
            style={{
                bottom: 16,
                right: 16,
                width: 320,
                maxHeight: 'calc(100vh - 32px)',
                background: 'rgba(11,14,20,0.97)',
                border: `1px solid ${color.c}66`,
                borderRadius: 4,
                boxShadow: `0 0 32px ${color.c}33, 0 12px 24px rgba(0,0,0,0.6)`,
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b"
                 style={{ borderColor: `${color.c}44`, background: `${color.c}15` }}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-bold"
                     style={{ color: color.c }}>
                    {PHASE_ICON(phase)}
                    <span data-testid="fd-status">{color.label}</span>
                    {creds?.extension &&
                        <span className="font-mono opacity-75">· {creds.extension}</span>}
                </div>
                <div className="flex items-center gap-0.5">
                    <button
                        data-testid="floating-dialer-fullview"
                        onClick={() => navigate('/softphone')}
                        title="Otevřít plný softphone"
                        className="p-1 hover:bg-white/10"
                        style={{ color: '#94a3b8' }}
                    >
                        <ArrowsOut size={12} weight="bold" />
                    </button>
                    <button
                        data-testid="floating-dialer-collapse"
                        onClick={() => setOpen(false)}
                        title="Sbalit"
                        className="p-1 hover:bg-white/10"
                        style={{ color: '#94a3b8' }}
                    >
                        <X size={12} weight="bold" />
                    </button>
                </div>
            </div>

            {/* No-creds state */}
            {!creds && !credsError && (
                <div className="p-6 text-center text-xs" style={{ color: '#64748b' }}>
                    <SpinnerGap size={18} className="animate-spin mx-auto mb-2" />
                    Načítám klapku…
                </div>
            )}

            {/* Body */}
            {creds && (
                <>
                    {/* Active-call card OR dest input */}
                    {inCall ? (
                        <div className="p-4 text-center"
                             style={{ background: 'rgba(34,197,94,0.05)' }}>
                            <div className="text-[10px] uppercase tracking-[0.2em] mb-1"
                                 style={{ color: '#94a3b8' }}>
                                {hasIncoming ? 'Příchozí hovor' :
                                 phase === 'ringing' ? 'Vyzvání' : 'Aktivní'}
                            </div>
                            <div className="text-lg font-mono font-bold truncate"
                                 data-testid="fd-remote"
                                 style={{ color: '#e2e8f0' }}>
                                {remote || '—'}
                            </div>
                            {phase === 'in_call' && (
                                <div className="text-sm font-mono mt-0.5"
                                     style={{ color: color.c }} data-testid="fd-duration">
                                    {fmt(duration)}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="px-3 pt-3">
                            <div className="flex items-center gap-1.5">
                                <input
                                    data-testid="fd-dest-input"
                                    value={dest}
                                    onChange={(e) => setDest(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && phase === 'registered' && dest.trim()) {
                                            placeCall();
                                        }
                                    }}
                                    placeholder="Číslo / klapka…"
                                    className="flex-1 bg-transparent border-b py-1.5 px-1 text-lg font-mono text-center focus:outline-none"
                                    style={{ borderColor: '#22c55e44', color: '#e2e8f0' }}
                                />
                                {dest && (
                                    <button
                                        data-testid="fd-backspace"
                                        onClick={() => setDest((d) => d.slice(0, -1))}
                                        className="p-1.5 hover:bg-white/10"
                                        style={{ color: '#94a3b8', borderRadius: 2 }}
                                        title="Smazat"
                                    >
                                        <Backspace size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Compact dialpad */}
                    {!hasIncoming && (
                        <div className="grid grid-cols-3 gap-1.5 p-3">
                            {DIAL_ROWS.flat().map((d) => (
                                <button
                                    key={d}
                                    data-testid={`fd-key-${d === '*' ? 'star' : d === '#' ? 'hash' : d}`}
                                    onClick={() => sendDtmf(d)}
                                    className="py-2 text-xl font-light active:scale-95 hover:bg-white/5 transition-colors"
                                    style={{
                                        color: '#e2e8f0',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                        borderRadius: 2,
                                    }}
                                >
                                    {d}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="p-3 pt-1 grid grid-cols-2 gap-2">
                        {phase === 'idle' || phase === 'failed' ? (
                            <button
                                data-testid="fd-register-btn"
                                onClick={register}
                                className="col-span-2 py-2 text-xs font-bold uppercase tracking-[0.1em]"
                                style={{ background: '#22c55e', color: '#0B0E14', borderRadius: 2 }}
                            >
                                Připojit
                            </button>
                        ) : hasIncoming ? (
                            <>
                                <button
                                    data-testid="fd-reject-btn"
                                    onClick={rejectCall}
                                    className="py-2.5 text-xs font-bold uppercase flex items-center justify-center gap-1.5"
                                    style={{ background: '#ef4444', color: '#fff', borderRadius: 2 }}
                                >
                                    <PhoneSlash size={14} weight="fill" /> Odmítnout
                                </button>
                                <button
                                    data-testid="fd-accept-btn"
                                    onClick={acceptCall}
                                    className="py-2.5 text-xs font-bold uppercase flex items-center justify-center gap-1.5"
                                    style={{ background: '#22c55e', color: '#0B0E14', borderRadius: 2 }}
                                >
                                    <Phone size={14} weight="fill" /> Přijmout
                                </button>
                            </>
                        ) : inCall ? (
                            <button
                                data-testid="fd-hangup-btn"
                                onClick={hangup}
                                className="col-span-2 py-2.5 text-xs font-bold uppercase flex items-center justify-center gap-2"
                                style={{ background: '#ef4444', color: '#fff', borderRadius: 2 }}
                            >
                                <PhoneSlash size={14} weight="fill" /> Zavěsit
                            </button>
                        ) : (
                            <>
                                <button
                                    data-testid="fd-open-fullview"
                                    onClick={() => navigate('/softphone')}
                                    className="py-2 text-[11px] uppercase tracking-[0.1em] flex items-center justify-center gap-1"
                                    style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', borderRadius: 2 }}
                                >
                                    Plný klient <CaretRight size={11} />
                                </button>
                                <button
                                    data-testid="fd-call-btn"
                                    onClick={() => placeCall()}
                                    disabled={!dest.trim() || phase !== 'registered'}
                                    className="py-2 text-xs font-bold uppercase tracking-[0.1em] flex items-center justify-center gap-1.5 disabled:opacity-30"
                                    style={{ background: '#22c55e', color: '#0B0E14', borderRadius: 2 }}
                                >
                                    <Phone size={14} weight="fill" /> Volat
                                </button>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default FloatingDialer;
